/**
 * CopyAction — compact, keyboard-visible copy control shared by chat surfaces.
 *
 * Only reports success after the browser clipboard API resolves. This avoids a
 * false “Copied” state in insecure contexts or browsers that deny clipboard
 * access.
 */
import * as React from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/cn";

export interface CopyActionProps {
  text: string;
  ariaLabel: string;
  className?: string;
}

export function CopyAction({ text, ariaLabel, className }: CopyActionProps) {
  const [copied, setCopied] = React.useState(false);
  const resetTimer = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    };
  }, []);

  const copy = React.useCallback(() => {
    if (!navigator.clipboard?.writeText) return;

    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
        resetTimer.current = window.setTimeout(() => {
          setCopied(false);
          resetTimer.current = null;
        }, 1200);
      })
      .catch(() => {
        // Browsers can reject clipboard access outside a user-activation or
        // in an insecure context. Keep the action quiet and truthful.
        setCopied(false);
      });
  }, [text]);

  return (
    <button
      type="button"
      onClick={copy}
      className={cn(
        "flex items-center gap-1 text-[10px] text-fg-faint transition-colors hover:text-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus",
        className,
      )}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {copied ? <Check size={11} aria-hidden /> : <Copy size={11} aria-hidden />}
      <span aria-hidden>{copied ? "Copied" : "Copy"}</span>
      {/* The static label names the action; this announces that it worked. */}
      <span role="status" className="sr-only">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </button>
  );
}
